import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dashboardService, { Reservation } from '../../services/dashboardService';

interface ReservationTableProps {
  startDate?: string;
  endDate?: string;
}

const STATUS_COLORS: Record<string, 'primary' | 'success' | 'info' | 'error' | 'warning' | 'default'> = {
  'Booked': 'primary',
  'Checked In': 'success',
  'Checked Out': 'info',
  'Cancelled': 'error',
  'No Show': 'warning',
};

const PROPERTY_CODE_MAPPING: Record<string, string> = {
  'TB': 'Abad Turtle Beach',
  'AW': 'Abad Whispering Palms',
  'AL': 'Abad Brook Side',
  'GF': 'Abad GreenForest',
  'CC': 'Abad Copper Castle',
  'HR': 'Abad Harmonia',
  'AF': 'Abad Fort',
  'DB': 'Abad Dutch Bungalow',
  'OC': 'Abad Old Court House',
  'AN': 'Ayur On the Beach',
  'AP': 'Abad Plaza',
  'AA': 'Abad Atrium',
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  return new Date(dateString + (dateString.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const formatDateTime = (dateTimeString: string | null) => {
  if (!dateTimeString) return null;
  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch {
    return null;
  }
};

const ReservationTable: React.FC<ReservationTableProps> = ({ startDate, endDate }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cities = await dashboardService.getReservationCities();
        if (!cancelled) setCityOptions(cities);
      } catch {
        if (!cancelled) setCityOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const result = await dashboardService.getReservations(
        statusFilter || undefined,
        propertyFilter || undefined,
        limit,
        searchQuery || undefined,
        offset,
        startDate,
        endDate,
        cityFilter || undefined
      );
      setReservations(result.reservations);
      setTotalCount(result.total_count);
      const pages = Math.ceil(result.total_count / limit);
      setTotalPages(pages);
      if (page > pages && pages > 0) setPage(1);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch reservations:', err);
      setError('Could not load reservation data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, propertyFilter, cityFilter, limit, page, searchQuery, startDate, endDate]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage);
  const handleLimitChange = (e: SelectChangeEvent) => { setLimit(Number(e.target.value)); setPage(1); };
  const handleStatusChange = (e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(1); };
  const handlePropertyChange = (e: SelectChangeEvent) => { setPropertyFilter(e.target.value); setPage(1); };
  const handleCityChange = (e: SelectChangeEvent) => { setCityFilter(e.target.value); setPage(1); };
  const handleSearchKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { setPage(1); fetchReservations(); } };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      await dashboardService.exportReservationsCsv({
        status: statusFilter || undefined,
        propertyId: propertyFilter || undefined,
        city: cityFilter || undefined,
        search: searchQuery || undefined,
        startDate,
        endDate,
      });
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Reservations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCount > 0
                ? `Showing ${startItem}–${endItem} of ${totalCount}`
                : 'No reservations found'}
              {startDate && endDate ? ` · checked in ${startDate === endDate ? startDate : `${startDate} to ${endDate}`}` : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search guest, booking ID..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKey}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ width: 220 }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(v => !v)}
            >
              Filters
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Box>
        </Box>

        {/* Collapsible filters */}
        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={handleStatusChange}>
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="Checked In">Checked In</MenuItem>
                <MenuItem value="Checked Out">Checked Out</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
                <MenuItem value="No Show">No Show</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Property</InputLabel>
              <Select value={propertyFilter} label="Property" onChange={handlePropertyChange}>
                <MenuItem value="">All Properties</MenuItem>
                {Object.entries(PROPERTY_CODE_MAPPING).map(([code, name]) => (
                  <MenuItem key={code} value={code}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>City</InputLabel>
              <Select value={cityFilter} label="City" onChange={handleCityChange}>
                <MenuItem value="">All cities</MenuItem>
                {cityOptions.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={limit.toString()} label="Rows" onChange={handleLimitChange}>
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
              <Table size="small" aria-label="reservations table">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Guest</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Arrival</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Departure</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Check-in Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Check-out Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reservations.length > 0 ? (
                    reservations.map((res) => (
                      <TableRow
                        key={res.id}
                        onClick={() => navigate(`/reservations/${res.id}`)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                          '&:last-child td': { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.primary.main }}>
                            {res.crs_no || `#${res.id}`}
                          </Typography>
                          {res.booked_by && (
                            <Typography variant="caption" color="text.secondary">
                              via {res.booked_by}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{res.guest_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{res.guest_city || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{res.property || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(res.arrival_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(res.departure_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          {formatDateTime(res.check_in_time) ? (
                            <Typography variant="body2" sx={{ color: theme.palette.success.dark }}>
                              {formatDateTime(res.check_in_time)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(res.check_out_time) ? (
                            <Typography variant="body2" sx={{ color: theme.palette.warning.dark }}>
                              {formatDateTime(res.check_out_time)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={res.reservation_status}
                            color={STATUS_COLORS[res.reservation_status] ?? 'default'}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No reservations found for the selected date range and filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalCount > 0 && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 2.5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReservationTable;
